import React, { useRef } from 'react';
import { Box, Flex } from '@react-three/flex';
import { useFrame, useThree } from '@react-three/fiber';
import { useGlobal } from '../../../logic/main';
import { boardConfig } from '../../../config/boardConfig';
import { Quaternion, Vector3 } from 'three';
import { CameraConfig } from '../../../config/models';

Vector3.prototype.equals = function(v, epsilon = 0.05) {
    return Math.abs(v.x - this.x) < epsilon && Math.abs(v.y - this.y) < epsilon && Math.abs(v.z - this.z) < epsilon;
};

Quaternion.prototype.equals = function(v, epsilon = 0.05) {
    return (
        Math.abs(v.x - this.x) < epsilon &&
        Math.abs(v.y - this.y) < epsilon &&
        Math.abs(v.z - this.z) < epsilon &&
        Math.abs(v.w - this.w) < epsilon
    );
};

export const Environment: React.FC = ({ children }) => {
    const { viewport } = useThree();

    const [
        { currentCameraConfig, currentAssetConfigs, orbitControlsDisabled, isTransforming },
        { toggleOrbitControls, setObjectsToTransform }
    ] = useGlobal(
        state => ({
            currentCameraConfig: state.currentCameraConfig,
            orbitControlsDisabled: state.orbitControlsDisabled,
            currentAssetConfigs: state.objectsToTransform,
            isTransforming: state.isTransforming
        }),
        actions => ({
            setObjectsToTransform: actions.setObjectsToTransform,
            toggleOrbitControls: actions.toggleOrbitControls,
            setOrbitCameraConfig: actions.setOrbitCameraConfig,
            setCameraConfig: actions.setCameraConfig
        })
    );

    let cameraLookAt = useRef<Vector3>();

    useFrame((state, delta) => {
        const { position, quaternion, target } = currentCameraConfig;
        const step = delta * boardConfig.cameraSpeed;
        if (cameraLookAt.current === undefined) {
            cameraLookAt.current = target!;
        }
        if (!boardConfig.debug) {
            state.camera.lookAt(cameraLookAt.current!.lerp(target!, step));
        }
        if (!isTransforming && !!orbitControlsDisabled && !position.equals(state.camera.position)) {
            state.camera.quaternion.slerp(quaternion, step);
            state.camera.position.lerp(position, step);
            state.camera.lookAt(cameraLookAt.current!.lerp(target!, step));
        } else if (orbitControlsDisabled && !isTransforming && position.equals(state.camera.position)) {
            toggleOrbitControls(true);
            cameraLookAt.current = target!;
        }
        currentAssetConfigs.forEach((config, index) => {
            const assetToTransform = boardConfig.assets.find(a => a.id === config.name)?.ref;
            if (assetToTransform && !config.position!.equals(assetToTransform.position)) {
                assetToTransform.position.lerp(config.position!, step);
                assetToTransform.quaternion.normalize().slerp(config.quaternion!.normalize(), step);
                assetToTransform.updateMatrixWorld();
            } else {
                const arrayWithoutFinishedTransform: CameraConfig[] = JSON.parse(JSON.stringify(currentAssetConfigs));
                delete arrayWithoutFinishedTransform[index];
                setObjectsToTransform(arrayWithoutFinishedTransform);
            }
        });
    });

    return (
        <group>
            <Flex dir="column" size={[viewport.width, viewport.height, 0]}>
                <Box dir="row" width="100%" height="100%" align="center" justify="center">
                    <group>{children}</group>
                </Box>
            </Flex>
        </group>
    );
};
